import Dropdown from '@afosto/components-react/Dropdown';
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Title from '@afosto/components-react/Title';
import Breadcrumbs from '@afosto/components-react/Breadcrumbs';
import Button from '@material-ui/core/Button';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import Tabs from '@afosto/components-react/Tabs';
import Popper from '@afosto/components-react/Popper/Popper';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Tooltip from '@material-ui/core/Tooltip';
import clsx from 'clsx';
import Icon from '../Icon';

export const PageHeaderBase = styled.div`
  padding: ${props => props.theme.spacing(2)}px;

  &:not(.collapsed) {
    padding-top: ${props => props.theme.spacing(2)}px;
    padding-bottom: ${props => props.theme.spacing(2)}px;
  }
`;

export const PageHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PageHeader = ({
  title,
  titleHidden,
  breadcrumbs,
  primaryAction,
  secondaryActions,
  subNavigation,
  linkComponent,
}) => {
  const breadcrumbMarkup = breadcrumbs && (
    <Breadcrumbs breadcrumbs={breadcrumbs} linkComponent={linkComponent} />
  );

  const getPrimaryActionMarkup = () => {
    if (!primaryAction) {
      return undefined;
    }

    const hasMultipleActions = primaryAction.actions && primaryAction.actions.length > 0;

    return hasMultipleActions ? (
      <Dropdown
        variant="button"
        label={primaryAction.text}
        position="bottom-end"
        disablePortal={false}
        items={primaryAction.actions}
        icon={primaryAction.icon}
      />
    ) : (
      <Button
        variant="contained"
        color="primary"
        onClick={primaryAction.action}
        disabled={primaryAction.disabled}
      >
        {primaryAction.text}
      </Button>
    );
  };

  const primaryActionMarkup = getPrimaryActionMarkup();

  const [secondaryActionsIsOpen, setSecondaryActionsIsOpen] = useState(false);
  const secondaryActionsButtonRef = useRef(null);

  const handleToggle = () => setSecondaryActionsIsOpen(!secondaryActionsIsOpen);
  const handleCloseSecondaryActions = e => {
    if (secondaryActionsButtonRef.current.contains(e.target)) {
      return;
    }
    setSecondaryActionsIsOpen(false);
  };

  const shortcutSecondaryActions =
    secondaryActions?.length > 0
      ? secondaryActions
          .filter(menuItem => menuItem.shortCut)
          .map(({ key, icon, action, text, color }) => {
            const handleOnClick = event => {
              action(event);
            };

            return (
              <Tooltip title={text ?? ''} aria-label={text} key={`${key}_shortcut`}>
                <IconButton onClick={handleOnClick} color={color}>
                  <Icon icon={icon} size="small" />
                </IconButton>
              </Tooltip>
            );
          })
      : null;

  const secondaryActionsMarkup = secondaryActions?.length > 0 && (
    <Box display="flex" alignItems="center">
      {shortcutSecondaryActions}
      <IconButton onClick={handleToggle} buttonRef={secondaryActionsButtonRef}>
        <Icon icon={['fal', 'ellipsis-v']} size="small" />
      </IconButton>
      <Popper
        handleClose={handleCloseSecondaryActions}
        anchorEl={secondaryActionsButtonRef}
        open={secondaryActionsIsOpen}
        position="bottom-end"
      >
        <ClickAwayListener onClickAway={handleCloseSecondaryActions}>
          <MenuList>
            {secondaryActions.map(({ text, action, color, disabled, icon, isLoading }) => {
              const onClick = e => {
                handleCloseSecondaryActions(e);
                action();
              };

              return (
                <MenuItem dense key={text} onClick={onClick} disabled={disabled}>
                  {icon && (
                    <ListItemIcon>
                      <Icon icon={icon} size="small" color={color} />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={text}
                    primaryTypographyProps={{ color: color || 'initial' }}
                  />
                  {isLoading && (
                    <ListItemSecondaryAction>
                      <CircularProgress size={15} />
                    </ListItemSecondaryAction>
                  )}
                </MenuItem>
              );
            })}
          </MenuList>
        </ClickAwayListener>
      </Popper>
    </Box>
  );

  const titleMarkup = <Title title={title} titleHidden={titleHidden} />;

  const subNavigationMarkup = subNavigation ? (
    <Tabs
      current={subNavigation.current}
      tabs={subNavigation.tabs}
      onChange={subNavigation.onChange}
    />
  ) : (
    <div />
  );

  return primaryAction ? (
    <PageHeaderBase>
      <Grid container justify="space-between" alignItems="center">
        <div>
          {breadcrumbMarkup}
          {titleMarkup}
        </div>
        {primaryActionMarkup}
      </Grid>
      <Grid container alignItems="center" justify="space-between">
        {subNavigationMarkup}
        {secondaryActionsMarkup}
      </Grid>
    </PageHeaderBase>
  ) : (
    <PageHeaderBase
      className={clsx({
        collapsed: !breadcrumbMarkup && (!titleMarkup || titleHidden) && !secondaryActionsMarkup,
      })}
    >
      {breadcrumbMarkup}
      {titleMarkup}
      {(subNavigation || secondaryActionsMarkup) && (
        <Grid container justify="space-between" alignItems="center">
          {subNavigationMarkup}
          {secondaryActionsMarkup}
        </Grid>
      )}
    </PageHeaderBase>
  );
};

const breadcrumbShape = PropTypes.shape({
  label: PropTypes.string,
  path: PropTypes.string,
  active: PropTypes.bool,
});

const secondaryActionShape = PropTypes.shape({
  text: PropTypes.string,
  action: PropTypes.func,
  disabled: PropTypes.bool,
  icon: PropTypes.array,
  isLoading: PropTypes.bool,
});

const subNavigationShape = PropTypes.shape({
  label: PropTypes.string,
  path: PropTypes.string,
});

const defaultPrimaryActionShape = {
  text: PropTypes.string,
  action: PropTypes.func,
  disabled: PropTypes.bool,
  icon: PropTypes.array,
};

PageHeader.propTypes = {
  title: PropTypes.string.isRequired,
  titleHidden: PropTypes.bool,
  breadcrumbs: PropTypes.arrayOf(breadcrumbShape),
  linkComponent: PropTypes.oneOfType([
    PropTypes.element,
    // eslint-disable-next-line react/no-typos
    PropTypes.elementType,
    PropTypes.node,
    PropTypes.string,
  ]),
  primaryAction: PropTypes.shape({
    text: PropTypes.string,
    action: PropTypes.func,
    disabled: PropTypes.bool,
    icon: PropTypes.array,
    actions: PropTypes.arrayOf(PropTypes.shape(defaultPrimaryActionShape)),
  }),
  secondaryActions: PropTypes.arrayOf(secondaryActionShape),
  subNavigation: PropTypes.shape({
    current: PropTypes.string,
    tabs: PropTypes.arrayOf(subNavigationShape),
    onChange: PropTypes.func,
  }),
};

PageHeader.defaultProps = {
  titleHidden: false,
  breadcrumbs: undefined,
  primaryAction: undefined,
  secondaryActions: undefined,
  subNavigation: undefined,
  linkComponent: 'a',
};

export default PageHeader;
